import { SelectChangeEvent } from '@mui/material/Select';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  deDE,
  gridClasses,
} from '@mui/x-data-grid';
import { FC, useEffect } from 'react';

import { BuchungsZeile } from '@codefluegel/wowis-rest-typescript-client';
import { Box, Typography, alpha, styled } from '@mui/material';
import { autorun } from 'mobx';
import { observer, useLocalObservable } from 'mobx-react-lite';
import moment from 'moment';
import { useBuchungApi } from '../../ApiClient';
import { numberFormatter } from '../../utils';
import BookingsTableToolbar from './BookingsTableToolbar';

const ODD_OPACITY = 0.1;
const SGK_YEAR_TO_START_DROPDOWN = 2024;

const StripedDataGrid = styled(DataGrid)(({ theme }) => ({
  borderRadius: '3px',
  borderWidth: 0,
  borderColor: theme.palette.background.paper,
  backgroundColor: theme.palette.secondary.light,

  '& .MuiDataGrid-columnHeaders': {
    fontSize: 16,
    color: theme.palette.secondary.dark,
  },

  // customise cell borders
  '& .MuiDataGrid-cell': {
    borderLeft: `3px solid ${theme.palette.divider}`,
    borderTop: `none`,
    borderBottom: `none`,
  },

  // remove left border from left column
  [`& .${gridClasses.cell}.noLeftBorder`]: {
    borderLeft: 'none',
  },

  // change background color of even rows
  [`& .${gridClasses.row}.even`]: {
    backgroundColor: theme.palette.background.paper,
    '&:hover, &.Mui-hovered': {
      backgroundColor: alpha(theme.palette.secondary.dark, ODD_OPACITY),
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
  },
  // change background color of odd rows
  [`& .${gridClasses.row}.odd`]: {
    '&:hover, &.Mui-hovered': {
      backgroundColor: alpha(theme.palette.secondary.dark, ODD_OPACITY),
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
  },
}));

interface Props {
  customerId: number;
  preview?: boolean;
}

const BookingsTable: FC<Props> = observer(props => {
  const { customerId, preview } = props;

  const isSgk = process.env.REACT_APP_CLIENT === 'sgk';

  // BuchungApi
  const buchungApi = useBuchungApi();

  const calculateBalance = (lines: BuchungsZeile[] | null | undefined) => {
    const calcBalanceToBePaid =
      lines?.reduce(
        (acc, cur) =>
          cur.soll_haben === 'Soll' || cur.soll_haben === 'Storno Soll'
            ? acc + cur.umsatz
            : acc + 0,
        0,
      ) || 0;
    return Math.round((calcBalanceToBePaid + Number.EPSILON) * 100) / 100;
  };

  const calculatePaid = (lines: BuchungsZeile[] | null | undefined) => {
    const balancePaid =
      lines?.reduce(
        (acc, cur) => (cur.soll_haben === 'Haben' ? acc + cur.umsatz : acc + 0),
        0,
      ) || 0;
    return Math.round((balancePaid + Number.EPSILON) * 100) / 100;
  };

  const localState = useLocalObservable(() => ({
    buchungenInitialized: false,
    buchungsZeilen: [] as BuchungsZeile[],
    setBuchungsZeilen(data: BuchungsZeile[]) {
      this.buchungenInitialized = true;
      this.buchungsZeilen = data;
    },
    chosenYear: Number(new Date().getFullYear()),
    setChosenYear(year: number) {
      this.chosenYear = year;
    },
    get transformedBuchungsZeilen() {
      let currentSaldo = 0;

      return this.buchungsZeilen
        .filter(entry =>
          this.chosenYear ? entry.jahr === this.chosenYear : entry,
        )
        .map(buchungsZeile => {
          currentSaldo += buchungsZeile.umsatz;

          const { soll_haben: sollHaben, umsatz } = buchungsZeile;
          let zuZahlenDisplay = 0;
          let eingezahltDisplay = 0;

          if (sollHaben === 'Soll') {
            zuZahlenDisplay = Math.abs(umsatz);
          } else if (sollHaben === 'Storno Soll') {
            zuZahlenDisplay = umsatz;
          }

          if (sollHaben === 'Haben') {
            eingezahltDisplay = Math.abs(umsatz);
          } else if (sollHaben === 'Storno Haben') {
            eingezahltDisplay = umsatz;
          }

          const bookingRow = {
            ...buchungsZeile,
            zu_zahlen: zuZahlenDisplay,
            eingezahlt: eingezahltDisplay,
            currentSaldo,
          };
          return bookingRow;
        });
    },
    get overallPaid() {
      return calculatePaid(this.transformedBuchungsZeilen);
    },
    get overallBalance() {
      return calculateBalance(localState.transformedBuchungsZeilen);
    },
    get availableYears() {
      const years = Array.from(new Set(this.buchungsZeilen.map(b => b.jahr)))
        .filter((y): y is number => y != null)
        .sort((a, b) => b - a);
      if (isSgk) {
        return years.filter(y => y >= SGK_YEAR_TO_START_DROPDOWN);
      }
      return years;
    },
    get tableEntries() {
      const filteredRows = this.transformedBuchungsZeilen;
      if (!preview) {
        return filteredRows || [];
      }
      return filteredRows.slice(0, 3) || [];
    },
  }));

  useEffect(
    () =>
      autorun(() => {
        async function fetchBuchungsData() {
          try {
            const { data } = await buchungApi.getAllMieterBuchungsZeilen(
              customerId,
            );

            localState.setBuchungsZeilen(data);
          } catch (error) {
            // TODO: add error handling
            console.log(error);
          }
        }
        fetchBuchungsData();
      }),
    [buchungApi, customerId],
  );

  const handleChange = (event: SelectChangeEvent) => {
    const yearAsNumber = parseInt(event.target.value, 10);
    localState.setChosenYear(yearAsNumber);
  };

  const CustomToolbar = observer(() => (
    <BookingsTableToolbar
      overallPaid={localState.overallPaid}
      overallBalance={localState.overallBalance}
      chosenYear={localState.chosenYear}
      availableYears={localState.availableYears}
      handleChange={handleChange}
    />
  ));

  const columnWidth = 800;
  const columnMinWidth = 100;

  const columns: GridColDef[] = [
    {
      field: 'beleg_datum',
      headerName: 'Datum',
      sortable: false,
      flex: 1,
      maxWidth: columnWidth,
      minWidth: columnMinWidth,
      valueFormatter: params => moment(params?.value).format('DD.MM.YYYY'),
      cellClassName: 'noLeftBorder',
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'buchungs_text',
      headerName: 'Buchungstext',
      flex: 1,
      minWidth: 150,
      editable: false,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Typography color="secondary" fontSize={12} variant="body2">
          {params.row.buchungs_text}
        </Typography>
      ),
    },
    {
      field: 'zu_zahlen',
      headerName: 'zu zahlen',
      flex: 1,
      maxWidth: columnWidth,
      minWidth: columnMinWidth,
      editable: false,
      headerAlign: 'center',
      align: 'center',
      valueFormatter: params => numberFormatter(params.value),
    },
    {
      field: 'eingezahlt',
      headerName: 'eingezahlt',
      flex: 1,
      maxWidth: columnWidth,
      minWidth: columnMinWidth,
      editable: false,
      headerAlign: 'center',
      align: 'center',
      valueFormatter: params => numberFormatter(params.value),
    },
    {
      field: 'currentSaldo',
      headerName: 'Saldo',
      flex: 1,
      maxWidth: columnWidth,
      minWidth: columnMinWidth,
      editable: false,
      headerAlign: 'center',
      align: 'center',
      valueFormatter: params => numberFormatter(params.value),
    },
  ];

  return (
    <Box width="100%" marginTop={3} id="datagrid">
      <StripedDataGrid
        localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
        rows={localState.tableEntries}
        hideFooterPagination={preview}
        columns={columns}
        disableRowSelectionOnClick
        getRowClassName={params =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }
        getCellClassName={params => {
          if (params.field === 'beleg_datum') {
            return 'noLeftBorder';
          }
          return '';
        }}
        slots={{ toolbar: preview ? null : CustomToolbar }}
      />
    </Box>
  );
});

export default BookingsTable;
